import * as React from "react";
import { api } from "../lib/api";

let CACHE = { brands: [], models: [], components: [], ts: 0 };
const TTL_MS = 60 * 1000;

function sortBrands(list = []) {
  return list.slice().sort((a, b) => {
    const ah = a.hasPricingInfo ? 1 : 0;
    const bh = b.hasPricingInfo ? 1 : 0;
    if (ah !== bh) return bh - ah;
    return a.brand.localeCompare(b.brand);
  });
}
function sortModels(list = []) {
  return list.slice().sort((a, b) => {
    const ba = a?.brand?.brand || "";
    const bb = b?.brand?.brand || "";
    if (ba !== bb) return ba.localeCompare(bb);
    return String(a.model).localeCompare(String(b.model));
  });
}

const PricingDataContext = React.createContext(null);

export function PricingDataProvider({ children }) {
  const boot = typeof window !== "undefined" ? window.__PRICELIST_BOOT__ : null;
  const bootFresh = !!(boot && Date.now() - (boot.ts || 0) < (boot.ttlMs || TTL_MS));
  const cacheFresh = Date.now() - CACHE.ts < TTL_MS;

  const initialBrands = bootFresh ? boot.brands : cacheFresh ? CACHE.brands : [];
  const initialModels = bootFresh ? boot.models : cacheFresh ? CACHE.models : [];
  const initialComponents = bootFresh ? (boot.components || []) : cacheFresh ? CACHE.components : [];

  // ✅ loadingInitial: uniquement si on n’a VRAIMENT rien à afficher
  const [loadingInitial, setLoadingInitial] = React.useState(!(bootFresh || cacheFresh));
  // ✅ refreshing: revalidation silencieuse en arrière-plan
  const [refreshing, setRefreshing] = React.useState(false);

  const [brands, setBrands] = React.useState(sortBrands(initialBrands));
  const [models, setModels] = React.useState(sortModels(initialModels));
  const [components, setComponents] = React.useState(initialComponents);
  const [error, setError] = React.useState("");

  const hasData = brands.length > 0 && models.length > 0;

  const revalidate = React.useCallback(async () => {
    setError("");
    // Si on n’a rien du tout -> on fait un chargement initial (affiché)
    // Sinon -> on rafraîchit en arrière-plan, SANS spinner bloquant
    if (!hasData) setLoadingInitial(true);
    else setRefreshing(true);

    try {
      const [b, m, c] = await Promise.all([
        api.get("/brands"),
        api.get("/models"),
        api.get("/components"),
      ]);
      const bSorted = sortBrands(b.data || []);
      const mSorted = sortModels(m.data || []);
      setBrands(bSorted);
      setModels(mSorted);
      const cList = Array.isArray(c.data) ? c.data : [];
      setComponents(cList);
      // Debug: allow verifying payload in devtools
      CACHE = { brands: bSorted, models: mSorted, components: cList, ts: Date.now() };
    } catch (e) {
      if (!hasData) {
        setError(e?.response?.data?.message || "Impossible de charger la liste des produits.");
      }
    } finally {
      setLoadingInitial(false);
      setRefreshing(false);
    }
  }, [hasData]);

  // Chargement au montage (+ profite du boot/cache)
  React.useEffect(() => { revalidate(); }, [revalidate]);

  const value = React.useMemo(
    () => ({
      brands,
      models,
      components,
      loading: loadingInitial, // ⚠️ utilisé par les composants pour afficher un spinner SEULEMENT si pas de données
      refreshing,              // info bonus si tu veux un petit indicateur discret
      error,
      refresh: revalidate,
    }),
    [brands, models, components, loadingInitial, refreshing, error, revalidate]
  );

  return <PricingDataContext.Provider value={value}>{children}</PricingDataContext.Provider>;
}

export function usePricingData() {
  const ctx = React.useContext(PricingDataContext);
  if (!ctx) throw new Error("usePricingData must be used within <PricingDataProvider>");
  return ctx;
}